/**
 * PBW Rewards
 * Keeps gift-selection behaviour separate from Warehouse theme.js.
 * Shopify remains responsible for validating and applying the actual discount.
 */
(function () {
  'use strict';

  function cartChangeUrl() {
    return `${window.routes.cartChangeUrl}.js`;
  }

  function cartAddUrl() {
    return `${window.routes.cartAddUrl}.js`;
  }

  function startLoading() {
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
  }

  function endLoading() {
    document.dispatchEvent(new CustomEvent('theme:loading:end'));
  }

  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { scrollToTop: false }
    }));
  }

  async function removeRewardGift(lineKey) {
    const response = await fetch(cartChangeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });

    if (!response.ok) {
      throw new Error('Unable to remove the existing reward gift.');
    }
  }

  async function addRewardGift(variantId) {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: {
            _pbw_reward_gift: '1'
          }
        }]
      })
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.description || 'Unable to add the reward gift.');
    }
  }

  function getExistingGiftKeys(panel) {
    return Array.from(panel.querySelectorAll('.pbw-existing-gift[data-line-key]'))
      .map((element) => element.dataset.lineKey)
      .filter(Boolean);
  }

  function setMessage(panel, message) {
    const messageElement = panel.querySelector('.pbw-gift-choice__message');
    if (messageElement) {
      messageElement.textContent = message || '';
    }
  }

  function setButtonsDisabled(panel, disabled) {
    panel.querySelectorAll('[data-pbw-gift-variant]').forEach((button) => {
      if (!button.classList.contains('is-selected')) {
        button.disabled = disabled;
      }
    });
  }

  async function chooseGift(button) {
    const panel = button.closest('[data-pbw-alc-promotion]');
    if (!panel || panel.dataset.unlocked !== 'true') return;

    const variantId = button.dataset.pbwGiftVariant;
    if (!variantId) return;

    startLoading();
    setButtonsDisabled(panel, true);
    setMessage(panel, 'Updating your free gift…');

    try {
      const existingKeys = getExistingGiftKeys(panel);
      for (const key of existingKeys) {
        await removeRewardGift(key);
      }

      await addRewardGift(variantId);
      refreshCart();
    } catch (error) {
      setMessage(panel, error.message || 'Unable to update your free gift. Please try again.');
      setButtonsDisabled(panel, false);
      endLoading();
    }
  }

  let productRewardsRefreshId = 0;

  async function refreshProductPageRewards() {
    const productSection = document.querySelector('[data-section-type="product"]');

    if (!productSection) return;

    const sectionId = productSection.getAttribute('data-section-id');
    if (!sectionId) return;

    const currentDeliveryBlock = productSection.querySelector('[data-block-type="pbw-delivery-reward"]');
    const currentAlcBlock = productSection.querySelector('[data-block-type="pbw-alc-promotion"]');

    if (!currentDeliveryBlock && !currentAlcBlock) return;

    const refreshId = ++productRewardsRefreshId;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to refresh product rewards.');
      }

      const html = await response.text();

      // Ignore an older response if another cart change happened meanwhile.
      if (refreshId !== productRewardsRefreshId) return;

      const holder = document.createElement('div');
      holder.innerHTML = html;

      const freshSection =
        holder.querySelector(`[data-section-id="${sectionId}"][data-section-type="product"]`) ||
        holder.querySelector('[data-section-type="product"]');

      if (!freshSection) return;

      const blockTypes = [
        'pbw-delivery-reward',
        'pbw-alc-promotion'
      ];

      blockTypes.forEach((blockType) => {
        const currentBlock = productSection.querySelector(`[data-block-type="${blockType}"]`);
        const freshBlock = freshSection.querySelector(`[data-block-type="${blockType}"]`);

        if (currentBlock && freshBlock) {
          currentBlock.innerHTML = freshBlock.innerHTML;
        } else if (currentBlock && !freshBlock) {
          // This can happen when a conditional PBW block no longer has anything to display.
          currentBlock.remove();
        }
      });
    } catch (error) {
      // Cart itself has already updated successfully, so do not interrupt the customer
      // if this secondary visual refresh ever fails.
      console.warn('PBW rewards: could not refresh product-page rewards.', error);
    }
  }

  async function removeInvalidRewardGifts() {
    const panels = Array.from(document.querySelectorAll('[data-pbw-alc-promotion][data-unlocked="false"]'));
    const keys = new Set();

    panels.forEach((panel) => {
      getExistingGiftKeys(panel).forEach((key) => keys.add(key));
    });

    if (!keys.size) return;

    startLoading();
    try {
      for (const key of keys) {
        await removeRewardGift(key);
      }
      refreshCart();
    } catch (error) {
      console.warn('PBW rewards: could not remove an ineligible reward gift.', error);
      endLoading();
    }
  }

  document.addEventListener('click', function (event) {
    const changeGiftButton = event.target.closest('[data-pbw-change-gift]');

    if (changeGiftButton) {
      event.preventDefault();

      const chooser = changeGiftButton.closest('[data-pbw-gift-choice]');
      const options = chooser ? chooser.querySelector('[data-pbw-gift-options]') : null;

      if (!options) return;

      const opening = options.hidden;
      options.hidden = !opening;
      changeGiftButton.setAttribute('aria-expanded', opening ? 'true' : 'false');
      changeGiftButton.textContent = opening ? 'Keep current gift' : 'Choose a different gift';
      return;
    }

    const button = event.target.closest('[data-pbw-gift-variant]');
    if (!button || button.disabled) return;

    event.preventDefault();
    chooseGift(button);
  });

  function onCartRerendered() {
    removeInvalidRewardGifts();
    refreshProductPageRewards();
  }

  // Warehouse dispatches cart:rerendered without bubbles on normal
  // storefront pages. Listening in the capture phase from <html>
  // catches mini-cart updates whether items are added, changed or removed.
  document.documentElement.addEventListener(
    'cart:rerendered',
    onCartRerendered,
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeInvalidRewardGifts);
  } else {
    removeInvalidRewardGifts();
  }
})();
